import { ReactNode, useEffect } from 'react';
import { useAppStore } from '@/store';
import { Sidebar } from '../sidebar/Sidebar';
import { ContextPanel } from '../context-panel/ContextPanel';
import { ResizeHandle } from './ResizeHandle';
import { cn } from '@/lib/utils';
import type { AppView } from '@/App';

export function AppLayout({ children, view, onViewChange }: {
  children: ReactNode;
  view?: AppView;
  onViewChange?: (v: AppView) => void;
}) {
  const sidebarWidth = useAppStore((s) => s.sidebarWidth);
  const contextWidth = useAppStore((s) => s.contextWidth);
  const setSidebarWidth = useAppStore((s) => s.setSidebarWidth);
  const setContextWidth = useAppStore((s) => s.setContextWidth);
  const activeCaseId = useAppStore((s) => s.activeCaseId);
  const mobileNavOpen = useAppStore((s) => s.mobileNavOpen);
  const mobileContextOpen = useAppStore((s) => s.mobileContextOpen);
  const setMobileNavOpen = useAppStore((s) => s.setMobileNavOpen);
  const setMobileContextOpen = useAppStore((s) => s.setMobileContextOpen);

  const drawerOpen = mobileNavOpen || mobileContextOpen;

  // Lock body scroll while a drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  // Close drawers on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileNavOpen(false);
        setMobileContextOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, setMobileNavOpen, setMobileContextOpen]);

  return (
    <div className="flex h-screen h-[100dvh] w-full bg-[var(--t-bg)] text-[var(--t-text)] font-sans overflow-hidden relative">
      {/* === Desktop sidebar (≥lg) === */}
      <div
        style={{ width: sidebarWidth }}
        className="hidden xl:flex shrink-0"
      >
        <Sidebar view={view} onViewChange={onViewChange} />
      </div>
      <div className="hidden xl:block">
        <ResizeHandle
          side="left"
          initialWidth={sidebarWidth}
          onChange={setSidebarWidth}
          min={180}
          max={520}
        />
      </div>

      {/* === Mobile sidebar drawer (<lg) === */}
      <div
        className={cn(
          'xl:hidden fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out',
          'w-[85vw] max-w-[320px] flex',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar view={view} onViewChange={onViewChange} />
      </div>

      {/* === Main column === */}
      <main className="flex-1 flex flex-col min-w-0 min-h-0 bg-[var(--t-bg)]">
        {children}
      </main>

      {/* === Desktop context panel === */}
      {activeCaseId && (
        <>
          <div className="hidden xl:block">
            <ResizeHandle
              side="right"
              initialWidth={contextWidth}
              onChange={setContextWidth}
              min={220}
              max={640}
            />
          </div>
          <div
            style={{ width: contextWidth }}
            className="hidden xl:flex shrink-0"
          >
            <ContextPanel />
          </div>
        </>
      )}

      {/* === Mobile context drawer === */}
      {activeCaseId && (
        <div
          className={cn(
            'xl:hidden fixed inset-y-0 right-0 z-40 transition-transform duration-200 ease-out',
            'w-[88vw] max-w-[360px] flex',
            mobileContextOpen ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          <ContextPanel />
        </div>
      )}

      {/* === Backdrop for any open mobile drawer === */}
      <div
        className={cn(
          'xl:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm transition-opacity duration-200',
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={() => { setMobileNavOpen(false); setMobileContextOpen(false); }}
      />
    </div>
  );
}
